import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import FilterBar from "../../components/primitives/FilterBar";
import AuditEventRow from "../../components/primitives/AuditEventRow";
import TextButton from "../../components/primitives/TextButton";
import { admin, ApiError } from "../../services/api";
import "../../styles/admin-shell.css";

const PAGE_SIZE = 50;

const PREFIX_CHIPS = [
    { key: "auth.", label: "Auth" },
    { key: "sku.", label: "SKU" },
    { key: "pack.", label: "Pack" },
    { key: "admin.", label: "Admin" },
    { key: "system.", label: "System" },
];

function readFiltersFromUrl(params) {
    return {
        prefix: params.get("action_prefix") || "",
        target_type: params.get("target_type") || "",
        offset: parseInt(params.get("offset") || "0", 10) || 0,
    };
}

function buildSearchParams(f) {
    const sp = new URLSearchParams();
    if (f.prefix) sp.set("action_prefix", f.prefix);
    if (f.target_type) sp.set("target_type", f.target_type);
    if (f.offset) sp.set("offset", String(f.offset));
    return sp;
}

function AdminAuditLogPage() {
    const [params, setParams] = useSearchParams();
    const [filters, setFilters] = useState(() => readFiltersFromUrl(params));
    const [data, setData] = useState({ events: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setParams(buildSearchParams(filters), { replace: true });
    }, [filters, setParams]);

    useEffect(() => {
        let cancelled = false;
        const ctrl = new AbortController();
        setLoading(true);
        setError(null);
        const query = { limit: PAGE_SIZE, offset: filters.offset || 0 };
        if (filters.prefix) query.action_prefix = filters.prefix;
        if (filters.target_type) query.target_type = filters.target_type;
        (async () => {
            try {
                const res = await admin.listEvents(query, { signal: ctrl.signal });
                if (!cancelled) setData(res);
            } catch (err) {
                if (cancelled || err?.name === "AbortError") return;
                setError(err instanceof ApiError ? err.message : err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [filters]);

    const chips = useMemo(
        () =>
            PREFIX_CHIPS.map((c) => ({
                ...c,
                active: filters.prefix === c.key,
            })),
        [filters.prefix]
    );

    const setPrefix = (next) =>
        setFilters((f) => ({
            ...f,
            prefix: f.prefix === next ? "" : next,
            offset: 0,
        }));

    const total = data.total || 0;
    const offset = filters.offset || 0;
    const showingFrom = data.events.length === 0 ? 0 : offset + 1;
    const showingTo = offset + data.events.length;
    const hasNext = offset + PAGE_SIZE < total;
    const hasPrev = offset > 0;

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div className="admin-page__title-block">
                    <span className="admin-page__eyebrow uppercase-label">
                        Admin / Audit log
                    </span>
                    <h1 className="admin-page__title">Audit log</h1>
                    <p className="admin-page__subtitle">
                        Append-only feed of every meaningful event. Filter by category.
                    </p>
                </div>
            </header>

            <div className="admin-page__body">
                <FilterBar
                    hideSearch
                    chips={chips}
                    onChipToggle={(key) => setPrefix(key)}
                />

                {error && (
                    <div className="admin-error" role="alert">
                        <AlertTriangle size={14} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                {loading && data.events.length === 0 ? (
                    <div className="admin-loading">Loading events…</div>
                ) : data.events.length === 0 ? (
                    <div className="admin-empty mono">
                        No events match these filters.
                    </div>
                ) : (
                    <div className="admin-feed__list">
                        {data.events.map((ev) => (
                            <AuditEventRow key={ev.id} event={ev} />
                        ))}
                    </div>
                )}

                <div className="admin-pagination">
                    <span>
                        {total === 0
                            ? "0 events"
                            : `${showingFrom}–${showingTo} of ${total}`}
                    </span>
                    <div className="admin-pagination__buttons">
                        <TextButton
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                                setFilters((f) => ({
                                    ...f,
                                    offset: Math.max(0, (f.offset || 0) - PAGE_SIZE),
                                }))
                            }
                            disabled={!hasPrev || loading}
                        >
                            Prev
                        </TextButton>
                        <TextButton
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                                setFilters((f) => ({
                                    ...f,
                                    offset: (f.offset || 0) + PAGE_SIZE,
                                }))
                            }
                            disabled={!hasNext || loading}
                        >
                            Next
                        </TextButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminAuditLogPage;
